import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import MetaControl from 'Components/UI/Meta/MetaControl';
import {
  FDBox, FDEmphasisSurface, FDStack,
} from '@facet-wealth/design';
import { BigQualifiedStat } from 'Views/Page/Content/Templates/planSummaryPageTemplate/facetDesignLike/bigQualifiedStat';
import Fac from 'Icons/Fac';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { FrostedGlassEffect } from 'Views/Page/Content/Templates/planSummaryPageTemplate/step/frostedGlassEffect';
import { LearnMoreHereLink } from 'Views/Page/Content/Templates/planSummaryPageTemplate/step/LearnMoreHereLink';
import { BodyTextSmall } from 'Packages/FacetDesign/Components';
import { summaryOverrides } from './summaryOverrides';

const useStyles = buildUseStyles('Summary', (theme) => ({
  fac: {
    color: theme.palette.facet.orange,
    fontSize: theme.spacing(13), // Matches the design
  },
}));

export function Summary({ resource }) {
  const classes = useStyles();
  const stats = useMemo(() => resource.attr('stats').resources().list(), [resource]);
  const isMobile = useMobileLayout();
  const content = (
    <FDStack direction="column" spacing={2} flex={1}>
      <MetaControl
        resource={resource}
        attrName="title"
        metaOverrideSet={summaryOverrides}
      />
      <FDStack spacing={4} wrap>
        <FDBox flex={1} minWidth={200}>
          <MetaControl
            resource={resource}
            attrName="milestoneOverview"
            metaOverrideSet={summaryOverrides}
            CustomProps={{ size: 'sm', flex: 1 }}
          />
        </FDBox>
        {stats.map(stat => (
          <FDBox key={stat?.id} flex={1} minWidth={200}>
            <BigQualifiedStat
              flex={1}
              topText={stat.attr('value')}
              bottomText={stat.attr('label')}
              qualifier={stat.attr('additionalInfo')}
              bigText="top"
            />
          </FDBox>
        ))}
        <FDBox flex={1} minWidth={200}>
          <MetaControl
            resource={resource}
            attrName="valueOverview"
            metaOverrideSet={summaryOverrides}
            CustomProps={{ size: 'sm', flex: 1 }}
          />
          <BodyTextSmall value={<LearnMoreHereLink />} />
        </FDBox>
      </FDStack>
    </FDStack>
  );

  // Keep it simple on mobile - the FrostedGlassEffect isn't perfectly tuned there just yet
  if (isMobile) {
    return <FDEmphasisSurface padding={4}>{content}</FDEmphasisSurface>;
  }

  // On desktop the FrostedGlassEffect works well, and we can add a Fac for some more visual flair
  return (
    <FDEmphasisSurface>
      <FrostedGlassEffect>
        <FDStack wrap justifyContent="space-between" spacing={4} alignItems="center" padding={8}>
          {content}
          <Fac className={classes.fac} />
        </FDStack>
      </FrostedGlassEffect>
    </FDEmphasisSurface>
  );
}
Summary.defaultProps = {
};

Summary.propTypes = {
  resource: PropTypes.object.isRequired,
};
